import { useMutation, useQueryClient } from '@tanstack/react-query';
import axios from 'axios';
import { E09Sample } from './types';

// Interface for creating a new sample
export interface CreateE09SampleRequest extends Partial<E09Sample> {
  fileId: number; // Required field for new sample creation
}

/**
 * Hook for adding a new E09 sample
 * 
 * @returns Mutation for creating a new E09 sample
 */
export const useAddE09Sample = () => {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: async (newSample: CreateE09SampleRequest) => {
      const { fileId, ...rest } = newSample;
      const payload = {
        ...rest,
        e09FileId: fileId,
      };
      
      const response = await axios.post('/data-sample/e09', payload);
      return response.data;
    },
    onSuccess: (data, variables) => {
      // Invalidate relevant queries to refetch the updated data
      queryClient.invalidateQueries({ 
        queryKey: ['e09Samples', variables.fileId] 
      });
    },
  });
};

/**
 * Hook for updating an existing E09 sample
 * 
 * @returns Mutation for updating an E09 sample
 */
export const useUpdateE09Sample = () => {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: async (updatedSample: E09Sample) => {
      const { fileId, ...rest } = updatedSample as any;
      const payload = {
        ...rest,
        e09FileId: fileId,
      };
      
      const response = await axios.put('/data-sample/e09', payload);
      return response.data;
    },
    onSuccess: (data, variables) => {
      // Invalidate relevant queries to refetch the updated data
      queryClient.invalidateQueries({ 
        queryKey: ['e09Samples'] 
      });
    },
  });
};

/**
 * Hook for deleting an E09 sample or amendment
 * 
 * @returns Mutation for deleting an E09 sample
 */
export const useDeleteE09Sample = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ sampleId, fileId }: { sampleId: string | number; fileId: number }) => {
      const response = await axios.delete(`/data-sample/e09`, {
        params: {
          sampleId,
          dataSetId: fileId,
        },
      });
      return response.data;
    },
    onSuccess: (data, variables) => {
      // Invalidate relevant queries to refetch the updated data
      queryClient.invalidateQueries({ 
        queryKey: ['e09Samples', variables.fileId] 
      });
    },
  });
}; 